import { useState } from "react";

import { makePostApiCall } from "../../../shared/makeApiCall";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import AddInventoryForm from "../../../components/forms/setup/product/AddInventoryForm";
import ImportMediaCsvDropzone from "../../../components/importMedia/ImportMediaCsvDropzone";
import ProgressModal from "../../../components/modals/ProgressModal";
import { generateUniqueNumber } from "../../../shared/utility";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";

export function ProductsViewModel(logout, addSnackbar) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState({
    data: [],
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [productsForDropdown, setProductsForDropdown] = useState([]);

  const showSnackbar = (message) => {
    addSnackbar(new SnackbarData(generateUniqueNumber(), message, SnackbarType.Success));
  };

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const fetchProductsForDropdown = async function (search, page) {
    let callResult = await makePostApiCall(`fetchProductsForDropdown`);
    if (callResult.success) {
      setProductsForDropdown(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getProducts = async function (search, page) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    let callResult = await makePostApiCall(`products`, payload);
    setLoading(false);
    if (callResult.success) {
      setProducts(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const deleteProduct = async function (id, index) {
    setLoading(true);

    let callResult = await makePostApiCall(`deleteProduct/${id}`, {});
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts(null, 0);
      showSnackbar(callResult.data.message);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const askDeleteProduct = function (product, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteProduct(product.id, index)}
          message={`Are you sure you want to delete ${product.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const uploadProductsCsv = async function (uploadedFile) {
    showProgressModal("Uploading products...");
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    let callResult = await makePostApiCall(
      "uploadProductsCsv",
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSnackbar(callResult.data.message);
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts("", 0);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    hideProgressModal();
  };

  const addInventoryApiCall = async function (values) {
    let callResult = await makePostApiCall(
      "addManufacturedProductInventory",
      values
    );

    if (callResult.success) {
      setSlideOver({
        childComponent: null,
        open: false,
      });
      showSnackbar(callResult.data.message);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const openImportProduct = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="productsCsv"
          actionCall={uploadProductsCsv}
          setOpen={setEmptyModelOpen}
          title="Products"
        />
      ),
      open: true,
    });
  };

  const uploadSunProductsPricesCsv = async function (uploadedFile) {
    showProgressModal("Uploading SUN products prices...");
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    let callResult = await makePostApiCall(
      "sunDistribution/updateProductsPrices",
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSnackbar(callResult.data.message);
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts("", 0);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    hideProgressModal();
  };

  const openSunImportPrices = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="productsCsv"
          actionCall={uploadSunProductsPricesCsv}
          setOpen={setEmptyModelOpen}
          title="Sun Products Price Import"
        />
      ),
      open: true,
    });
  };

  const uploadSunProductsCostsCsv = async function (uploadedFile) {
    showProgressModal("Uploading SUN products costs...");
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    let callResult = await makePostApiCall(
      "sunDistribution/updateProductsCosts",
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSnackbar(callResult.data.message);
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts("", 0);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    hideProgressModal();
  };

  const openSunImportCosts = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="productsCsv"
          actionCall={uploadSunProductsCostsCsv}
          setOpen={setEmptyModelOpen}
          title="Sun Products Cost Import"
        />
      ),
      open: true,
    });
  };

  const addInventory = () => {
    setSlideOver({
      childComponent: (
        <AddInventoryForm
          actionCall={addInventoryApiCall}
          productOptions={productsForDropdown}
          setOpen={setSlideOver}
        />
      ),
      open: true,
    });
  };

  return {
    askDeleteProduct,
    emptyModal,
    setEmptyModelOpen,
    getProducts,
    loading,
    products,
    setSlideOver,
    slideOver,
    openImportProduct,
    addInventory,
    fetchProductsForDropdown,
    openSunImportPrices,
    openSunImportCosts,
  };
}
